<template>
  <div v-if="show" class="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-50">
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
      <!-- 头部 -->
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-medium text-gray-900">{{ modalTitle }}</h3>
          <button @click="handleCancel" class="text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- 内容 -->
      <div class="px-6 py-4 max-h-96 overflow-y-auto">
        <p class="text-sm text-gray-600 mb-4">
          检测到 <span class="font-medium text-gray-900">{{ totalWords }}</span> 个敏感词，{{ confirmText }}：
        </p>

        <!-- 敏感词列表 -->
        <div class="space-y-2">
          <div 
            v-for="word in detectedWords" 
            :key="`${word.word}-${word.position}`"
            class="flex items-center justify-between py-2 px-3 bg-gray-50 rounded border"
          >
            <div class="flex items-center space-x-2">
              <span class="font-medium text-gray-900">{{ word.word }}</span>
              <span 
                class="px-2 py-0.5 text-xs rounded"
                :class="getSeverityClass(word.severity_level)"
              >
                {{ getSeverityLabel(word.severity_level) }}
              </span>
            </div>
            <span class="text-xs text-gray-500">
              {{ word.position === 'subject' ? '主题' : '内容' }}
            </span>
          </div>
        </div>

        <!-- 统计 -->
        <div v-if="totalWords > 1" class="mt-4 pt-3 border-t border-gray-200">
          <div class="flex items-center space-x-4 text-xs text-gray-600">
            <span v-if="statisticsSummary.high > 0">高风险: {{ statisticsSummary.high }}</span>
            <span v-if="statisticsSummary.medium > 0">中风险: {{ statisticsSummary.medium }}</span>
            <span v-if="statisticsSummary.low > 0">低风险: {{ statisticsSummary.low }}</span>
          </div>
        </div>
      </div>

      <!-- 底部按钮 -->
      <div class="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div class="flex items-center justify-end space-x-3">
          <button
            @click="handleCancel"
            :disabled="confirming"
            class="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            取消
          </button>
          <button
            @click="handleConfirm"
            :disabled="confirming"
            class="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-1"
          >
            <svg v-if="confirming" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            <span>{{ confirming ? confirmingText : confirmButtonText }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  sensitiveWordData: {
    type: Object,
    default: () => ({})
  },
  mode: {
    type: String,
    default: 'save', // 'save' 为保存模板模式, 'send' 为发送邮件模式
    validator: (value) => ['save', 'send'].includes(value)
  }
})

const emit = defineEmits(['close', 'confirm'])

const confirming = ref(false)

// 计算属性：提取敏感词列表
const detectedWords = computed(() => {
  if (!props.sensitiveWordData || !props.sensitiveWordData.detected_words) {
    return []
  }
  
  let words = props.sensitiveWordData.detected_words
  
  // 处理可能的 ErrorDetail 格式
  if (Array.isArray(words)) {
    return words.map(word => ({
      word: extractValue(word.word),
      severity_level: extractValue(word.severity_level),
      category_name: extractValue(word.category_name),
      match_type: extractValue(word.match_type),
      matched_text: extractValue(word.matched_text),
      position: extractValue(word.position),
      start_pos: extractValue(word.start_pos),
      end_pos: extractValue(word.end_pos),
      alternative_words: word.alternative_words || []
    }))
  }
  
  return []
})

// 计算属性：总敏感词数
const totalWords = computed(() => {
  return detectedWords.value.length
})

// 计算属性：统计信息
const statisticsSummary = computed(() => {
  const summary = { low: 0, medium: 0, high: 0, forbidden: 0 }
  
  detectedWords.value.forEach(word => {
    const level = word.severity_level || 'medium'
    if (summary[level] !== undefined) {
      summary[level]++
    }
  })
  
  return summary
})

// 计算属性：动态文字
const modalTitle = computed(() => {
  return props.mode === 'send' ? '敏感词检测' : '敏感词检测'
})

const confirmText = computed(() => {
  return props.mode === 'send' ? '请确认是否继续发送' : '请确认是否继续保存'
})

const confirmButtonText = computed(() => {
  return props.mode === 'send' ? '继续发送' : '继续保存'
})

const confirmingText = computed(() => {
  return props.mode === 'send' ? '发送中...' : '保存中...'
})

// 提取值的辅助函数（处理 ErrorDetail 格式）
const extractValue = (value) => {
  if (!value) return ''
  
  // 如果是 ErrorDetail 对象，提取字符串值
  if (typeof value === 'object' && value.string !== undefined) {
    return value.string
  }
  
  // 如果已经是字符串，直接返回
  if (typeof value === 'string') {
    return value
  }
  
  return String(value)
}

// 获取严重级别标签
const getSeverityLabel = (level) => {
  const labels = {
    low: '低风险',
    medium: '中风险', 
    high: '高风险',
    forbidden: '禁止'
  }
  return labels[level] || level
}

// 获取严重级别样式
const getSeverityClass = (level) => {
  const classes = {
    low: 'bg-green-100 text-green-700',
    medium: 'bg-yellow-100 text-yellow-700',
    high: 'bg-red-100 text-red-700',
    forbidden: 'bg-gray-800 text-white'
  }
  return classes[level] || 'bg-gray-100 text-gray-700'
}

// 处理确认
const handleConfirm = async () => {
  confirming.value = true
  try {
    emit('confirm')
  } finally {
    confirming.value = false
  }
}

// 处理取消
const handleCancel = () => {
  if (!confirming.value) {
    emit('close')
  }
}
</script>

<style scoped>
/* 简洁的滚动条 */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>